/**
 * What the device is holding, what it overwrote, and what cannot be sent.
 *
 * Three pieces of § 4, deliberately in one place so a manager has one thing to
 * read rather than three badges to correlate:
 *
 * **The header line** (§ 4.3, case 1) — online/offline and N pending. "Nothing is
 * lost and nothing is silently dropped" is a promise, and this is where it is kept
 * visible.
 *
 * **The reconciliation report** (§ 4.4) — after a pull-then-replay, every value
 * this device overwrote, with what staging held beside it. Last-write-wins is the
 * rule; this is the manager seeing the writes it won.
 *
 * **The frozen-session case** (§ 4.4, second half) — pending drafts for a session
 * staging will not accept are *never* flushed, so the app says what it is holding
 * and requires an explicit discard. The button names the number it will destroy,
 * because silently dropping a manager's typing is worse than a dialog.
 */
import { useState } from "react";

import { formatTimestamp } from "../sessions/format";
import type { DraftRecord } from "./db";
import type { FlushBlock, Overwrite } from "./reconcile";

export function SyncStatusLine({
  online,
  pending,
  refused,
  flushing,
}: {
  online: boolean;
  pending: number;
  refused: number;
  flushing: boolean;
}) {
  return (
    <div className="sync-line" data-testid="sync-status">
      <span className={online ? "chip chip--online" : "chip chip--offline"} data-testid="net-state">
        {online ? "online" : "offline"}
      </span>
      {flushing && <span className="sync sync--pending">syncing…</span>}
      {pending > 0 ? (
        <span className="sync sync--pending" data-testid="pending-count">
          {pending} held on this device
        </span>
      ) : (
        <span className="muted small" data-testid="pending-none">
          everything is in Business Central
        </span>
      )}
      {refused > 0 && (
        <span className="sync sync--refused" data-testid="refused-count">
          {refused} refused
        </span>
      )}
    </div>
  );
}

export function ReconciliationReport({
  overwrites,
  onDismiss,
}: {
  overwrites: Overwrite[];
  onDismiss: () => void;
}) {
  if (overwrites.length === 0) return null;

  return (
    <div className="card notice" data-testid="reconciliation">
      <h3>
        {overwrites.length} count{overwrites.length === 1 ? "" : "s"} on this device
        replaced what Business Central held
      </h3>
      <p className="small">
        This device's entries win — you counted the shelf. Nothing was merged and
        nothing was averaged; here is exactly what was replaced, in case one of them
        was somebody else's count on another iPad.
      </p>
      <table className="recon">
        <thead>
          <tr>
            <th>Item</th>
            <th>Was in BC</th>
            <th>Now</th>
            <th>Typed</th>
          </tr>
        </thead>
        <tbody>
          {overwrites.map((row) => (
            <tr key={row.item_no} data-testid={`overwrote-${row.item_no}`}>
              <td>
                <code>{row.item_no}</code>
              </td>
              <td>{row.staged ?? <span className="muted">not counted</span>}</td>
              <td>
                <strong>{row.local ?? "not counted"}</strong>
              </td>
              <td className="muted small">
                {formatTimestamp(row.typed_at)} · {row.device_id}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <button type="button" className="ghost" onClick={onDismiss} data-testid="dismiss-recon">
        I have read this
      </button>
    </div>
  );
}

/**
 * The blocked-flush case. Only rendered when something is actually held — a
 * warning about drafts that do not exist is noise that teaches people to ignore
 * warnings.
 */
export function BlockedFlushNotice({
  block,
  drafts,
  onDiscard,
}: {
  block: FlushBlock;
  drafts: DraftRecord[];
  onDiscard: () => Promise<number>;
}) {
  const [confirming, setConfirming] = useState(false);
  const [discarded, setDiscarded] = useState<number | null>(null);

  if (discarded !== null) {
    return (
      <div className="card" data-testid="discarded">
        <p>
          {discarded} held count{discarded === 1 ? "" : "s"} discarded from this device.
          Business Central was not changed.
        </p>
      </div>
    );
  }

  if (block.allowed || drafts.length === 0) return null;
  // Offline is not a problem to solve with a dialog: the queue is working as
  // designed and the connection will come back.
  if (block.reason === "offline" || block.reason === "nothing-pending") return null;

  return (
    <div className="card error" data-testid="blocked-flush">
      <h3>This device is holding {drafts.length} count{drafts.length === 1 ? "" : "s"} it cannot send</h3>
      <p>{block.message}</p>
      <ul className="held-list">
        {drafts.map((draft) => (
          <li key={draft.item_no} data-testid={`held-${draft.item_no}`}>
            <code>{draft.item_no}</code>{" "}
            {draft.counted_qty === null ? (
              <em>cleared — leave inventory unchanged</em>
            ) : (
              <strong>{draft.counted_qty}</strong>
            )}{" "}
            <span className="muted small">typed {formatTimestamp(draft.typed_at)}</span>
          </li>
        ))}
      </ul>
      <p className="small">
        Write down anything you still need before discarding — this is the only copy.
      </p>
      {!confirming ? (
        <button
          type="button"
          className="danger"
          onClick={() => setConfirming(true)}
          data-testid="discard-held"
        >
          Discard these {drafts.length} count{drafts.length === 1 ? "" : "s"}
        </button>
      ) : (
        <div className="confirm" data-testid="discard-confirm">
          <p>
            <strong>
              Throw away {drafts.length} count{drafts.length === 1 ? "" : "s"} typed on this
              device?
            </strong>
          </p>
          <p className="small">
            They were never sent to Business Central, so nothing there changes. They
            cannot be recovered.
          </p>
          <div className="confirm__buttons">
            <button
              type="button"
              className="danger"
              onClick={async () => setDiscarded(await onDiscard())}
              data-testid="discard-confirm-yes"
            >
              Yes, discard them
            </button>
            <button type="button" className="ghost" onClick={() => setConfirming(false)}>
              Keep holding them
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
